<template>
  <div class="popUpWrap">
    <section class="popup light">
      <div class="topArea">
        <h4>{{ t('organization-create-option-1') }}</h4>
        <div class="exit" @click.stop="dialogStore.close"></div>
      </div>

      <!-- selectBox 짤림 현상 방지 style 추가 -->
      <div class="scrollY" style="overflow: clip visible !important;">
        <ul class="infoList flex">
          <li class="flex">
            <div class="tit">{{ t('common-organization-name') }}</div>
            <input
              id="org-name"
              type="text"
              v-model="organizationName"
              :class="{ error: orgNameError }"
              :placeholder="t('common-placeholder-organization-name')"
              @input="isOrgNameTouched = true"
              @blur="isOrgNameTouched = true"
            >
            <p v-if="orgNameError" class="errorMessage"><span></span>{{ t('error-organization-name') }}</p>
          </li>
          <li class="flex">
            <div class="tit">{{ t('register-organization-size-title') }}</div>
            <div
              ref="rangePeopleSelectRef"
              class="selectBox"
              :class="{ on: isRangePeopleOpen }"
            >
              <button
                type="button"
                class="btnToggle"
                :class="{ selected: !!selectedRangePeople }"
                @click.stop="toggleRangePeopleDropDown"
              >
                {{ selectedRangePeopleLabel }}<span></span>
              </button>

              <ul class="dropMenu">
                <li
                  v-for="rangePeople in ORG_SIZE_RANGE_LIST"
                  :key="rangePeople"
                  :class="['dropItem', { active: rangePeople === selectedRangePeople }]"
                >
                  <button
                    type="button"
                    class="dropOption"
                    @click.stop="onClickRangePeople(rangePeople)"
                  >
                    {{ rangePeople }}
                  </button>
                </li>
              </ul>
            </div>
          </li>
        </ul>
        <div class="btnArea">
          <button
            class="btn"
            :class="{ disabled: !isFormValid }"
            @click.stop="onClickCreate"
          >
            {{ t('organization-create-button') }}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { useDialogStore } from "~/store/dialog/dialogStore";
import { useToastStore } from "~/store/toast/toastStore";
import { ToastType } from "~/components/Common/Toast/model/ToastType";
import { useOrganizationService } from "~/composables/service/useOrganizationService";
import { ORG_SIZE_RANGE_LIST, type OrgSizeRange } from "~~/package/mapper/organization/model/OrganizationRange";
import { useUserService } from "~/composables/service/useUserService";

const { t } = useI18n();
const dialogStore = useDialogStore();
const toastStore = useToastStore();
const organizationService = useOrganizationService();

const organizationName = ref('');
const selectedRangePeople = ref<OrgSizeRange | ''>('');

const isOrgNameTouched = ref(false);

const rangePeopleSelectRef = ref<HTMLElement | null>(null);
const isRangePeopleOpen = ref(false);

const orgNameError = computed(() => {
  if(!isOrgNameTouched.value) return false;

  const len = organizationName.value.trim().length;
  return len > 50 || len === 0;
});

const selectedRangePeopleLabel = computed(() => {
  return selectedRangePeople.value
    ? selectedRangePeople.value
    : t('register-organization-size-select');
});

const isFormValid = computed(() => {
  const name = organizationName.value.trim();
  return name.length > 0 && name.length <= 50 && !!selectedRangePeople.value;
});
const toggleRangePeopleDropDown = () => {
  isRangePeopleOpen.value = !isRangePeopleOpen.value;
};

const onClickRangePeople = (range: OrgSizeRange) => {
  selectedRangePeople.value = range;
  isRangePeopleOpen.value = false;
};

const onClickCreate = async () => {
  if (!isFormValid.value) return;

  try {
    const name = organizationName.value.trim();
    const size = selectedRangePeople.value;

    const res = await organizationService.createOrganization(name, size);

    if (res) {
      toastStore.openToast({
        toastType: ToastType.SUCCESS,
        contents: t('organization-create-success'),
      });

      await useUserService().setUser()
      dialogStore.options.confirm?.event();
    }
    else {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: convertToNextLine(t('organization-create-fail')),
      });
    }
  } catch (e) {
    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: convertToNextLine(t('organization-create-fail')),
    });
  } finally {
    dialogStore.close();
  }
};

onClickOutside(rangePeopleSelectRef, () => {
  isRangePeopleOpen.value = false;
});
</script>
